/**
 * /app/campaigns — Campaign Manager light (email blast).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Eye, Loader2, Mail, Send, Users, X } from "lucide-react";
import { toast } from "sonner";
import { CrudShellHeader } from "@/components/ds";
import { ClinicalPageShell } from "@/components/clinical";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { CampaignsTour } from "@/components/ProductTour";

export const Route = createFileRoute("/app/campaigns")({
  component: CampaignsPage,
  head: () => ({ meta: [{ title: "Campañas — Structura Market" }] }),
});

type Campaign = {
  id: string;
  name: string;
  subject: string;
  html: string;
  segment: "all" | "recurring" | "lapsed";
  status: string;
  sentCount: number;
  failedCount: number;
  openCount: number;
  clickCount: number;
  createdAt: string;
  sentAt: string | null;
};

const VARS: Array<{ key: string; desc: string }> = [
  { key: "{{customerName}}", desc: "Nombre del cliente" },
  { key: "{{tenantName}}", desc: "Nombre de tu tienda" },
];

const DEFAULT_HTML =
  "<p>Hola {{customerName}},</p>\n<p>Tenemos novedades para vos en {{tenantName}}.</p>";

function applyVars(html: string, name: string, tenant: string) {
  return html
    .replace(/\{\{customerName\}\}/g, name || "Cliente")
    .replace(/\{\{tenantName\}\}/g, tenant || "Tu tienda");
}

type EmailProvider = "gmail_oauth" | "resend" | "smtp" | "platform" | null;

const PROVIDER_INFO: Record<NonNullable<EmailProvider>, { label: string; dailyCap: number; hint: string }> = {
  gmail_oauth: { label: "Gmail", dailyCap: 450, hint: "Gmail permite hasta ~500 correos por día. Enviamos de a 5 con pausa entre tandas para no saturar tu cuenta." },
  resend:      { label: "Resend", dailyCap: Infinity, hint: "Resend es un proveedor de email transaccional. Los límites dependen de tu plan en Resend." },
  smtp:        { label: "SMTP", dailyCap: Infinity, hint: "Enviamos de a 10 con pausa entre tandas para respetar los límites de tu servidor SMTP." },
  platform:    { label: "Structura", dailyCap: 100, hint: "Usás el email de Structura. Las campañas consumen tu cuota mensual del plan." },
};

function CampaignsPage() {
  const tid = useWorkspaceTenantId();
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [emailProvider, setEmailProvider] = useState<EmailProvider>(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [segment, setSegment] = useState<"all" | "recurring" | "lapsed">("all");
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewSample, setPreviewSample] = useState<Array<{ email: string; name: string | null }>>([]);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [pendingSendId, setPendingSendId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [r, cfg] = await Promise.all([
        apiGetJson<{ campaigns: Campaign[] }>("/api/campaigns"),
        apiGetJson<{ provider?: string }>("/api/tenant/email").catch(() => null),
      ]);
      setCampaigns(r.campaigns ?? []);
      const p = cfg?.provider as EmailProvider | undefined;
      setEmailProvider(p ?? null);
    } catch (e) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) { setLock(err.planLock); return; }
      toast.error(e instanceof Error ? e.message : "Error al cargar campañas");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  const preview = async () => {
    setBusy(true);
    try {
      const r = await apiSendJson<{ count: number; sample: Array<{ email: string; name: string | null }> }>("/api/campaigns", "POST", {
        action: "preview_recipients",
        segment,
      });
      setPreviewCount(r.count);
      setPreviewSample(r.sample ?? []);
      toast.success(`${r.count} destinatarios en este segmento`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error preview");
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (!name.trim() || !subject.trim() || !html.trim()) {
      toast.error("Completá nombre, asunto y cuerpo");
      return;
    }
    setBusy(true);
    try {
      await apiSendJson("/api/campaigns", "POST", {
        action: "create",
        name: name.trim(),
        subject: subject.trim(),
        html: html.trim(),
        segment,
      });
      toast.success("Borrador creado");
      setName("");
      setSubject("");
      setHtml(DEFAULT_HTML);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al crear");
    } finally {
      setBusy(false);
    }
  };

  const confirmSend = (id: string) => {
    setPendingSendId(id);
    setShowSendDialog(true);
  };

  const executeSend = async () => {
    if (!pendingSendId) return;
    setShowSendDialog(false);
    setBusy(true);
    try {
      const r = await apiSendJson<{
        sent: number;
        failed: number;
        capped?: boolean;
        cappedByProvider?: boolean;
        recipientTotal?: number;
        maxBlast?: number;
        provider?: string;
      }>("/api/campaigns", "POST", { action: "send", id: pendingSendId });
      const capMsg = r.cappedByProvider
        ? ` — límite diario de tu proveedor alcanzado (${r.maxBlast} enviados de ${r.recipientTotal})`
        : r.capped
        ? ` — tope del plan (${r.maxBlast} de ${r.recipientTotal})`
        : "";
      const failMsg = r.failed > 0 ? `, ${r.failed} con error` : "";
      toast.success(`${r.sent} correos enviados${failMsg}${capMsg}`);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al enviar");
    } finally {
      setBusy(false);
      setPendingSendId(null);
    }
  };

  // Renderizar preview del HTML en el iframe
  const openPreview = () => {
    setShowPreview(true);
  };
  useEffect(() => {
    if (showPreview && iframeRef.current) {
      const rendered = applyVars(html, "María García", "Tu Tienda");
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:sans-serif;padding:20px;color:#111;line-height:1.6}p{margin:0 0 12px}</style></head><body>${rendered}</body></html>`);
        doc.close();
      }
    }
  }, [showPreview, html]);

  if (lock) return <FeatureLockedState payload={lock} />;

  const pendingCampaign = campaigns.find((c) => c.id === pendingSendId);

  return (
    <div>
      <CampaignsTour />
      <CrudShellHeader
        title="Campañas"
        description="Enviá correos a clientes que ya compraron en tu tienda."
        backHref="/app/clientes"
        backLabel="Volver a Clientes"
      />
      <ClinicalPageShell offset="none" className="pt-6">
        {emailProvider && PROVIDER_INFO[emailProvider] ? (
          <div className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${emailProvider === "platform" ? "border-amber-500/30 bg-amber-500/8 text-amber-900 dark:text-amber-200" : "border-border bg-muted/40 text-muted-foreground"}`}>
            <AlertCircle className={`mt-0.5 h-4 w-4 shrink-0 ${emailProvider === "platform" ? "text-amber-600" : "text-muted-foreground"}`} />
            <div className="space-y-1">
              <p className="font-medium text-foreground">
                Enviando desde: <span className="text-primary">{PROVIDER_INFO[emailProvider].label}</span>
                {PROVIDER_INFO[emailProvider].dailyCap !== Infinity && (
                  <span className="ml-2 text-xs font-normal opacity-70">— máx. {PROVIDER_INFO[emailProvider].dailyCap}/día</span>
                )}
              </p>
              <p className="text-xs">{PROVIDER_INFO[emailProvider].hint}</p>
              <p className="text-xs">Cada destinatario recibe su correo de forma individual con su nombre y su link de desuscripción.</p>
            </div>
          </div>
        ) : (
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/8 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs">Para enviar campañas configurá tu cuenta de email en <strong>Configuración → Notificaciones</strong>.</p>
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-2">
          <section className="space-y-3 rounded-xl border border-border bg-card/40 p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <Mail className="h-4 w-4" /> Nueva campaña
            </h2>
            <div className="space-y-2">
              <Label>Nombre interno</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Promo agosto" />
            </div>
            <div className="space-y-2">
              <Label>Asunto</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Novedades de la semana"
              />
            </div>
            <div className="space-y-2">
              <Label>Segmento</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={segment}
                onChange={(e) => setSegment(e.target.value as typeof segment)}
              >
                <option value="all">Todos con email</option>
                <option value="recurring">Recurrentes (≥2 pedidos)</option>
                <option value="lapsed">Inactivos (&gt;45 días)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Cuerpo (HTML)</Label>
              <textarea
                className="min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                value={html}
                onChange={(e) => setHtml(e.target.value)}
              />
              <div className="rounded-md border border-border bg-muted/40 px-3 py-2 space-y-1">
                <p className="text-[11px] font-medium text-muted-foreground">Variables disponibles:</p>
                {VARS.map((v) => (
                  <div key={v.key} className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <code className="rounded bg-muted px-1 py-0.5 text-[10px] font-mono">{v.key}</code>
                    <span>{v.desc}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" disabled={busy} onClick={() => void preview()}>
                <Users className="mr-1.5 h-4 w-4" />
                Ver destinatarios
                {previewCount != null ? ` (${previewCount})` : ""}
              </Button>
              <Button type="button" variant="outline" disabled={!html.trim()} onClick={openPreview}>
                <Eye className="mr-1.5 h-4 w-4" />
                Preview email
              </Button>
              <Button type="button" disabled={busy} onClick={() => void create()}>
                Guardar borrador
              </Button>
            </div>

            {/* Lista de destinatarios de muestra */}
            {previewSample.length > 0 && (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 space-y-1">
                <p className="text-[11px] font-medium text-muted-foreground">
                  Muestra ({previewSample.length} de {previewCount}):
                </p>
                {previewSample.map((r) => (
                  <p key={r.email} className="text-[11px] text-muted-foreground truncate">
                    {r.name ? `${r.name} ` : ""}<span className="opacity-60">{r.email}</span>
                  </p>
                ))}
                {(previewCount ?? 0) > previewSample.length && (
                  <p className="text-[11px] text-muted-foreground opacity-60">
                    +{(previewCount ?? 0) - previewSample.length} más…
                  </p>
                )}
              </div>
            )}
          </section>

          <section className="space-y-3 rounded-xl border border-border bg-card/40 p-4">
            <h2 className="text-sm font-semibold">Historial</h2>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
              </div>
            ) : campaigns.length === 0 ? (
              <p className="text-sm text-muted-foreground">Todavía no hay campañas.</p>
            ) : (
              <ul className="space-y-2">
                {campaigns.map((c) => {
                  const statusLabel = c.status === "draft" ? "Borrador"
                    : c.status === "sending" ? "Enviando…"
                    : c.status === "sent" ? "Enviada"
                    : "Con errores";
                  const statusColor = c.status === "sent" ? "text-green-700 bg-green-500/10"
                    : c.status === "failed" ? "text-red-700 bg-red-500/10"
                    : c.status === "sending" ? "text-blue-700 bg-blue-500/10"
                    : "text-muted-foreground bg-muted";
                  const segmentLabel = c.segment === "all" ? "Todos con email"
                    : c.segment === "recurring" ? "Recurrentes"
                    : "Inactivos";
                  return (
                    <li key={c.id} className="rounded-lg border border-border px-3 py-2.5 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium">{c.name}</span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor}`}>{statusLabel}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {c.subject} · {segmentLabel}
                        {c.sentCount > 0 ? ` · ${c.sentCount} enviados` : ""}
                        {c.failedCount > 0 ? ` · ${c.failedCount} fallidos` : ""}
                        {c.openCount > 0 ? ` · ${c.openCount} aperturas` : ""}
                        {c.clickCount > 0 ? ` · ${c.clickCount} clicks` : ""}
                      </p>
                      {(c.status === "draft" || c.status === "failed") ? (
                        <Button size="sm" className="mt-2" disabled={busy} onClick={() => confirmSend(c.id)}>
                          <Send className="mr-1.5 h-3.5 w-3.5" /> Enviar
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </ClinicalPageShell>

      {/* Dialog de confirmación de envío */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar envío</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                {pendingCampaign ? (
                  <>
                    <p>
                      Vas a enviar <strong>"{pendingCampaign.name}"</strong> a{" "}
                      <strong>{pendingCampaign.segment === "all" ? "todos tus clientes con email" : pendingCampaign.segment === "recurring" ? "clientes recurrentes" : "clientes inactivos"}</strong>.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Los correos se envían de a 10 por vez con pausa entre tandas para proteger tu cuenta de email. El proceso puede tardar varios minutos si tenés muchos destinatarios.
                    </p>
                  </>
                ) : (
                  <p>¿Enviás esta campaña?</p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>Cancelar</Button>
            <Button onClick={() => void executeSend()}>
              <Send className="mr-1.5 h-4 w-4" /> Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de preview del email */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Preview del email
              <button type="button" className="rounded p-1 hover:bg-muted" onClick={() => setShowPreview(false)}>
                <X className="h-4 w-4" />
              </button>
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              Las variables se muestran con valores de ejemplo. El email real incluye el header/footer de tu marca.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-border overflow-hidden" style={{ height: 320 }}>
            <iframe
              ref={iframeRef}
              title="Preview email"
              className="w-full h-full bg-white"
              sandbox="allow-same-origin"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
